/**
 * LinguistPro — Lightweight Toast Notification
 * SPEC-019 · UX Polish
 *
 * Singleton event-emitter pattern — no React context needed.
 * Usage:
 *   import { showToast } from '../components/Toast';
 *   showToast('Deleted.', 'success');
 *   showToast('Failed.', 'error');
 *
 * Render <ToastContainer /> once in AppLayout.
 */

import React, { useState, useEffect, useCallback } from 'react';

export type ToastLevel = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  message: string;
  level: ToastLevel;
}

const LP_TOAST_EVENT = 'lp:toast';
let _nextId = 0;

/** Dispatch a toast notification. Safe to call anywhere (no React context needed). */
export function showToast(message: string, level: ToastLevel = 'info'): void {
  window.dispatchEvent(
    new CustomEvent<ToastItem>(LP_TOAST_EVENT, {
      detail: { id: ++_nextId, message, level },
    }),
  );
}

function toastBg(level: ToastLevel): string {
  switch (level) {
    case 'success': return 'bg-[#00d4b8]';
    case 'error':   return 'bg-red-500';
    default:        return 'bg-slate-700';
  }
}

/** Renders the toast stack (fixed bottom-right). Add once in AppLayout. */
export function ToastContainer(): React.JSX.Element {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    function onToast(e: Event) {
      const item = (e as CustomEvent<ToastItem>).detail;
      setToasts(prev => [...prev.slice(-4), item]); // cap at 5 concurrent toasts
      setTimeout(() => dismiss(item.id), 4000);
    }
    window.addEventListener(LP_TOAST_EVENT, onToast);
    return () => window.removeEventListener(LP_TOAST_EVENT, onToast);
  }, [dismiss]);

  return (
    <div
      aria-live="polite"
      className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none"
    >
      {toasts.map(t => (
        <div
          key={t.id}
          role="alert"
          className={`${toastBg(t.level)} text-white text-sm font-medium px-4 py-2.5 rounded-lg shadow-lg pointer-events-auto
            transition-all duration-300 ease-out animate-[slideIn_0.2s_ease-out]`}
        >
          {t.message}
        </div>
      ))}
    </div>
  );
}
